import { Box, Flex } from '@mantine/core';
import type {
	EntityViolationsOut,
	PolicyOut,
	PolicyRuleType,
	PolicyViolationOut,
} from '@repo/api-codegen';
import { Text } from '@repo/foundations';
import { observer } from 'mobx-react-lite';
import { useFeatureFlags } from '../../../../utils/featureFlags';
import { ResourceDisplay } from '../../../PoliciesPage/render';
import PolicySection from '../../PolicySection';
import { ConditionsNotMet } from './ConditionBadge';
import { useStyles } from './styles';
import { ViolationStatusBadge } from './ViolationStatusBadge';
import { ViolationTableEmptyState } from './ViolationTableEmptyState';
import { ViolationTableFooter } from './ViolationTableFooter';
import { PolicyTableLoadingState } from './ViolationTableLoadingState';
import { ViolationTableTitle } from './ViolationTableTitle';

import { formatDateTimeWithAt } from '../../../../utils/time';
import { PolicyColumnTable } from './PolicyColumnTable/PolicyColumnTable';
import policyIssueStore from './store';
interface PolicyViolationsTableProps {
	violations: EntityViolationsOut[];
	onResourceClick: (
		id: string,
		parent_result_id?: string | null,
		parent_url?: string | null,
		policyType?: PolicyRuleType,
		violations?: PolicyViolationOut[]
	) => void;
	showScoreColumn?: boolean;
	isLoading?: boolean;
	page?: number;
	totalCount?: number;
	hasNextPage?: boolean;
	hasPreviousPage?: boolean;
	onPageChange?: (newPage: number) => void;
	radius?: string | number;
	withBorder?: boolean;
	policy: PolicyOut;
}

export const PolicyViolationsTable = observer(
	({
		violations,
		onResourceClick,
		showScoreColumn = false,
		isLoading = false,
		page = 1,
		totalCount = 0,
		hasNextPage = false,
		hasPreviousPage = false,
		onPageChange,
		radius = 'md',
		withBorder = true,
		policy,
	}: PolicyViolationsTableProps) => {
		const { dataScanning } = useFeatureFlags();
		const { classes } = useStyles();

		return (
			<PolicySection
				withBorder={withBorder}
				radius={radius}
				spacing={false}
				title={
					<ViolationTableTitle
						showScoreColumn={showScoreColumn}
						dataScanning={dataScanning}
					/>
				}
				content={
					<>
						{isLoading && (
							<PolicyTableLoadingState showScoreColumn={showScoreColumn} />
						)}
						{!isLoading && violations.length === 0 && (
							<ViolationTableEmptyState />
						)}
						{!isLoading && violations.length > 0 && (
							<Flex
								h="50vh"
								direction="column"
								p={0}
								sx={{ overflowY: 'auto' }}
							>
								{violations.map((violation) => (
									<>
										<Box
											key={violation.entity.id}
											className={classes.rowContainer}
											sx={(theme) => ({
												backgroundColor:
													policyIssueStore.selectedEntityId ===
													violation.entity.id
														? theme.other.getColor('surface/primary/active')
														: undefined,
											})}
											onClick={() =>
												onResourceClick(
													violation.entity.id,
													violation.violations[0].id,
													violation.entity.url,
													violation.violations.length > 0
														? violation.violations[0].policy_rule.type
														: undefined,
													violation.violations
												)
											}
										>
											<Flex className={classes.resourceColumn}>
												<ResourceDisplay
													record={violation.entity as any}
													includeLeftSideIcon={
														dataScanning &&
														violation.violations.some(
															(v) => v.policy_rule.type === 'PII_SCAN'
														)
													}
													leftSideIconName={
														policyIssueStore.selectedEntityId ===
														violation.entity.id
															? 'chevronDown'
															: 'chevronRight'
													}
												/>
											</Flex>
											{dataScanning && (
												<Flex className={classes.statusColumn}>
													<ViolationStatusBadge
														violations={violation.violations}
													/>
												</Flex>
											)}
											<Flex className={classes.conditionsColumn}>
												<ConditionsNotMet violations={violation.violations} />
											</Flex>
											{showScoreColumn && (
												<Flex className={classes.scoreColumn}>
													<Text size="sm">
														{violation.total_score || 0} points
													</Text>
												</Flex>
											)}
											<Flex className={classes.dateColumn}>
												{violation.violations[0].created_at && (
													<Text size="sm" truncate>
														{formatDateTimeWithAt(
															violation.violations[0].created_at
														)}
													</Text>
												)}
											</Flex>
										</Box>
										{policyIssueStore.selectedEntityId ===
											violation.entity.id &&
											policyIssueStore.columnViolations &&
											policyIssueStore.columnViolations.length > 0 && (
												<PolicyColumnTable
													columns={policyIssueStore.columnViolations}
													policy={policy}
												/>
											)}
									</>
								))}
							</Flex>
						)}
					</>
				}
				footer={
					<ViolationTableFooter
						isLoading={isLoading}
						page={page}
						totalCount={totalCount}
						hasPreviousPage={hasPreviousPage}
						hasNextPage={hasNextPage}
						onPageChange={onPageChange}
					/>
				}
			/>
		);
	}
);
